import React, { useMemo } from 'react';
import { AgentLastRunLabel } from '@/experiences/sales-portal/domain/pricing-agent/screens/components/agent-last-run-label';
import { createStyles } from '@/contexts/theme-context';
import { PricingSummaryHeaderDetails } from '@sales-portal/domain/pricing/screens/management/components/pricing-summary-header-details';
import { RecommendationAction } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store-types';
import { RecommendationGroup } from '@/experiences/sales-portal/domain/pricing-agent/screens/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { usePricingSummaryHeaderLayout } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-summary-header-layout';
import { useRecommendationActionsStore } from '@/experiences/sales-portal/domain/pricing-agent/blocs/stores/recommendation-actions-store';
import { View } from 'react-native';

interface Props {
  currencyCode: string;
  defaultAccepted: boolean;
  groups: RecommendationGroup[];
  lastCompletedAt: string | null;
  title: string;
}

export const AgentReviewHeader: React.FC<Props> = ({
  currencyCode,
  defaultAccepted,
  groups,
  lastCompletedAt,
  title,
}) => {
  const [styles] = useStyles();
  const {
    containerStyle,
    handleLayout,
    headingVariant,
    isStacked,
    wrapperStyle,
  } = usePricingSummaryHeaderLayout();

  const actions = useRecommendationActionsStore((state) => state.actions);

  const {
    itemCount,
    restaurantCount,
    totalRecWeeklyImpactInMicros,
    totalWeeklyImpactInMicros,
    totalWeeklyVolume,
  } = useMemo(() => {
    // Groups may be keyed by either dimension, so count both off the leaves.
    const restaurantUUIDs = new Set<string>();
    const variantPackUUIDs = new Set<string>();
    const totals = groups.reduce(
      (acc, group) => {
        acc.totalWeeklyVolume += group.aggregateWeeklyVolume;
        acc.totalRecWeeklyImpactInMicros +=
          group.aggregateWeeklyGPImpactInMicros;

        for (const row of group.subRows) {
          restaurantUUIDs.add(row.restaurantUUID);
          variantPackUUIDs.add(row.variantPackUUID);
          const action = actions[row.uuid]?.action ?? row.reviewStatus;
          const isAccepted =
            action === RecommendationAction.Accepted ||
            action === RecommendationAction.Overridden ||
            (defaultAccepted && action === null);
          if (!isAccepted) {
            continue;
          }
          const override = actions[row.uuid]?.overridePriceInMicros;
          const priceDeltaInMicros =
            override !== undefined ? override - row.newPriceInMicros : 0;
          acc.totalWeeklyImpactInMicros +=
            (row.weeklyGPImpactInMicros ?? 0) +
            priceDeltaInMicros * (row.weeklyVolume ?? 0);
        }
        return acc;
      },
      {
        totalRecWeeklyImpactInMicros: 0,
        totalWeeklyImpactInMicros: 0,
        totalWeeklyVolume: 0,
      },
    );
    return {
      ...totals,
      itemCount: variantPackUUIDs.size,
      restaurantCount: restaurantUUIDs.size,
    };
  }, [actions, defaultAccepted, groups]);

  return (
    <View onLayout={handleLayout} style={containerStyle}>
      <View style={wrapperStyle}>
        <View style={styles.titleColumn}>
          <Typography variant="heading-lg">{title}</Typography>
          <AgentLastRunLabel lastCompletedAt={lastCompletedAt} />
        </View>
        <PricingSummaryHeaderDetails
          currencyCode={currencyCode}
          editsImpact={totalWeeklyImpactInMicros}
          editsImpactLabelKey="pricing.agent.review.weekly_gp_impact"
          headingVariant={headingVariant}
          isStacked={isStacked}
          recImpact={totalRecWeeklyImpactInMicros}
          recImpactLabelKey="pricing.agent.review.rec_gross_profit_impact"
          restaurantCount={restaurantCount}
          totalWeeklyVolume={totalWeeklyVolume}
          variantPackCount={itemCount}
        />
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  titleColumn: {
    gap: theme.sk.spacing.spaceXsmall,
  },
}));
